/**
 * Cobertura produto × projeto — onde falta conteúdo.
 *
 * Uma linha por modelo do projeto, uma coluna por sistema, e no cruzamento o
 * conteúdo que existe. **O que interessa é o vazio**: é ali que falta trabalho.
 * Por isso a célula cheia é discreta e a vazia é que tem cor.
 *
 * **O relatório equivalente do sistema antigo devolve grade vazia sempre.** Ele
 * procura o conteúdo por dois caminhos e os dois morreram — `dado_versao` tem
 * 320 linhas e nenhuma com sistema; `dado_item` tem zero linhas com
 * `modelo_id > 0` entre as que ainda apontam para um conteúdo existente. A tela
 * antiga não dá erro: mostra tudo "-", e quem olha conclui que não há conteúdo
 * nenhum. Esta lê de `TabelaPesquisa`, o índice que o site usa de verdade.
 *
 * **Escolher sistema é obrigatório, e é a decisão da tela.** São 87 sistemas; a
 * grade inteira não cabe nem é útil. Quem monta a grade sabe quais sistemas
 * importam para aquele produto.
 */

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obterCobertura, type Cobertura as Dados } from "@/api/cobertura"
import { obter } from "@/api/cliente"
import { usarAssuntos, usarProdutosDeConteudo } from "@/api/catalogosDeConteudo"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { Aviso, Botao, Campo, estiloCampo, mensagem } from "../outros/pecas"

type Pedido = {
  projetoId: number
  produtoId: number
  assuntoId: number
  sistemaIds: number[]
  baseId: number
}

export function Cobertura() {
  const produtos = usarProdutosDeConteudo()
  const assuntos = usarAssuntos()

  /**
   * O catálogo de sistemas, para escolher as colunas.
   *
   * **O nome vem sempre da base 1, e não dá para escolher.** `sistema` é
   * chaveado por (id, base) no banco — 75 ids existem nas duas —, mas a entidade
   * do EF usa só o id: a listagem colapsa o par e devolve 1.662 linhas, todas
   * marcadas como base 1. Filtrar por base aqui esvaziaria o seletor na base 2.
   *
   * Para esta tela o efeito é pequeno: o **id** é o mesmo nas duas bases, e é o
   * id que monta a grade. O que pode divergir é o texto da coluna, que na base 2
   * às vezes é escrito diferente. A grade em si sai certa, porque a API resolve
   * os nomes pela base pedida.
   *
   * O sistema 0, de nome vazio, fica de fora: é o "sem sistema" do banco, não
   * uma coluna.
   */
  const sistemasBrutos = useQuery({
    queryKey: ["cobertura-sistemas"] as const,
    queryFn: () => obter<{ id: number; nome: string; baseId: number }[]>("api/sistemas", {}),
    staleTime: Infinity,
  })

  const [projetoId, setProjetoId] = useState("")
  const [produtoId, setProdutoId] = useState("")
  const [assuntoId, setAssuntoId] = useState("")
  const [baseId, setBaseId] = useState(1)
  const [escolhidos, setEscolhidos] = useState<number[]>([])
  const [pedido, setPedido] = useState<Pedido | null>(null)

  const sistemas = useMemo(() => {
    return (sistemasBrutos.data ?? [])
      .filter((s) => s.id > 0 && (s.nome ?? "").trim() !== "")
      .map((s) => {
        const nome = s.nome.trim()
        // Tres sistemas em producao chamam-se literalmente "0" -- 465, 466 e 1258.
        // Repetidos na lista seriam tres linhas iguais que ninguem distingue; com o
        // id na frente da para escolher o certo e, depois, achar e arrumar.
        return { id: s.id, rotulo: nome === "0" ? `#${s.id} (sem nome)` : nome }
      })
      .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"))
  }, [sistemasBrutos.data])

  const grade = useQuery({
    queryKey: ["cobertura", pedido] as const,
    queryFn: () => obterCobertura(pedido!),
    enabled: pedido != null,
  })

  const pronto =
    Number(projetoId) > 0 && Number(produtoId) >= 0 && produtoId !== "" &&
    Number(assuntoId) > 0 && escolhidos.length > 0

  function montar() {
    if (!pronto) return
    setPedido({
      projetoId: Number(projetoId),
      produtoId: Number(produtoId),
      assuntoId: Number(assuntoId),
      sistemaIds: escolhidos,
      baseId,
    })
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Cobertura produto × projeto"
        detalhe={grade.data
          ? `${grade.data.preenchidas} de ${grade.data.celulas} células têm conteúdo`
          : "Onde falta conteúdo, por modelo e por sistema"}
      >
        <Botao destaque aoClicar={montar} desligado={!pronto} ocupado={grade.isFetching}>
          {grade.isFetching ? "Montando…" : "Montar a grade"}
        </Botao>
      </Faixa>

      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)", background: "var(--surface)",
      }}>
        <Campo rotulo="Projeto" ajuda="O projeto define as linhas — um modelo por linha.">
          <input value={projetoId} onChange={(e) => setProjetoId(e.target.value.replace(/\D/g, ""))}
                 placeholder="id do projeto" style={{ ...estiloCampo, width: 130 }} />
        </Campo>

        <Campo rotulo="Produto">
          <select value={produtoId} onChange={(e) => setProdutoId(e.target.value)}
                  style={{ ...estiloCampo, width: 200 }}>
            <option value="">escolha…</option>
            {produtos.itens.map((p) => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
          </select>
        </Campo>

        <Campo rotulo="Assunto">
          <select value={assuntoId} onChange={(e) => setAssuntoId(e.target.value)}
                  style={{ ...estiloCampo, width: 220 }}>
            <option value="">escolha…</option>
            {assuntos.itens.map((a) => <option key={a.id} value={a.id}>{a.rotulo}</option>)}
          </select>
        </Campo>

        <Campo rotulo="Base">
          <select value={baseId} onChange={(e) => { setBaseId(Number(e.target.value)); setEscolhidos([]) }}
                  style={{ ...estiloCampo, width: 90 }}>
            <option value={1}>1</option>
            <option value={2}>2</option>
          </select>
        </Campo>

        <Campo
          rotulo={`Sistemas (${escolhidos.length})`}
          ajuda={`As colunas. São ${sistemas.length} no catálogo — a grade inteira não cabe nem ajuda. ` +
                 "O nome vem da base 1; o id é o mesmo nas duas, e a grade resolve os nomes pela base pedida."}
        >
          <select
            multiple
            value={escolhidos.map(String)}
            onChange={(e) =>
              setEscolhidos([...e.target.selectedOptions].map((o) => Number(o.value)))}
            style={{ ...estiloCampo, width: 260, height: 96 }}
          >
            {sistemas.map((s) => <option key={s.id} value={s.id}>{s.rotulo}</option>)}
          </select>
        </Campo>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {!pedido ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "62ch", lineHeight: 1.6 }}>
            Escolha o projeto, o produto, o assunto e ao menos um sistema. A grade sai numa
            consulta — o sistema antigo dispara uma por célula, e num projeto de 159 modelos com
            8 sistemas isso são 1.272 idas ao banco para desenhar uma tela.
          </p>
        ) : grade.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Montando…</p>
        ) : grade.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(grade.error)}</p>
        ) : grade.data ? (
          <Grade d={grade.data} />
        ) : null}
      </div>

      <BarraDeEstado
        direita={grade.data
          ? `${grade.data.modelos} modelos × ${grade.data.sistemas} sistemas`
          : undefined}
      >
        {grade.data
          ? `${grade.data.celulas - grade.data.preenchidas} célula(s) sem conteúdo — é onde falta trabalho.`
          : "O que interessa nesta tela é o vazio."}
      </BarraDeEstado>
    </div>
  )
}

function Grade({ d }: { d: Dados }) {
  /** Índice (modelo, sistema) → conteúdo, para a célula não varrer a lista. */
  const porCelula = useMemo(() => {
    const m = new Map<string, { dadoId: number; dado: string }>()
    for (const c of d.conteudos) m.set(`${c.modeloId}:${c.sistemaId}`, { dadoId: c.dadoId, dado: c.dado })
    return m
  }, [d.conteudos])

  const vazias = d.celulas - d.preenchidas

  return (
    <>
      <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600 }}>
        {d.projeto} · {d.produto} · {d.assunto}
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 11.5, color: "var(--txt2)" }}>
        base {d.baseId} · {d.preenchidas} de {d.celulas} preenchidas ·{" "}
        <strong style={{ color: vazias > 0 ? "var(--erro)" : "var(--ok)" }}>
          {vazias} sem conteúdo
        </strong>
      </p>

      {d.avisos.map((a, i) => <Aviso key={i}>{a}</Aviso>)}

      <div style={{ overflow: "auto", border: "1px solid var(--line2)", borderRadius: 6 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr>
              <th style={{
                ...cabeca, position: "sticky", left: 0, zIndex: 2, minWidth: 240,
                background: "var(--surface2, var(--surface))",
              }}>
                Modelo
              </th>
              {d.colunas.map((c) => (
                <th key={c.sistemaId} style={{ ...cabeca, minWidth: 90, maxWidth: 130 }}>
                  {c.sistema || `#${c.sistemaId}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.linhas.map((l) => (
              <tr key={l.modeloId}>
                <td style={{
                  ...celula, position: "sticky", left: 0, zIndex: 1, whiteSpace: "nowrap",
                  background: "var(--surface2, var(--surface))",
                }}>
                  {l.modelo || `#${l.modeloId}`}
                  {l.motor && <span style={{ color: "var(--txt3)" }}> · {l.motor}</span>}
                </td>
                {d.colunas.map((c) => {
                  const tem = porCelula.get(`${l.modeloId}:${c.sistemaId}`)
                  return (
                    <td
                      key={c.sistemaId}
                      title={tem ? tem.dado : "Sem conteúdo"}
                      style={{
                        ...celula, textAlign: "center",
                        // O vazio e que tem cor: e ele que a tela existe para achar.
                        background: tem ? undefined : "color-mix(in srgb, var(--erro) 12%, transparent)",
                        color: tem ? "var(--ok)" : "var(--erro)",
                      }}
                    >
                      {tem ? "•" : "—"}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

const cabeca: React.CSSProperties = {
  padding: "5px 8px", fontSize: 10.5, fontWeight: 600, color: "var(--txt2)",
  borderBottom: "1px solid var(--line)", textAlign: "left", verticalAlign: "bottom",
  position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
}

const celula: React.CSSProperties = {
  padding: "3px 8px", borderBottom: "1px solid var(--line2)",
}
